import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { logError } from "shared/services/ErrorReporting";
import { useAnalytics } from "contexts/AnalyticsContext";
import { FirebaseError } from "firebase/app";
import { Button } from "components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import GoogleLogo from "assets/svg/google-logo.svg?react";
import { toast } from "utils/toast";
import { AuthErrorCodes } from "firebase/auth";
import { CircleHelp } from "lucide-react";
import ReedyLogo from "assets/svg/reedy-logo.svg?react";
import { Text } from "components/text";
import { LoginForm } from "./components/LoginForm";

export type ErrorCodes = (typeof AuthErrorCodes)[keyof typeof AuthErrorCodes];

export const Login: React.FC = () => {
  const { t } = useTranslation(["general", "login"]);

  const { signInWithOauth } = useAuth();

  const [errorLogin, setErrorLogin] = useState<Error>();
  const { gaEvent } = useAnalytics();

  const resetErrorLogin = () => setErrorLogin(undefined);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetErrorLogin only clears state; the toast should fire once per error
  useEffect(() => {
    if (errorLogin) {
      toast.error(t("login:error.loginFailed"), {
        onAction: resetErrorLogin,
      });
    }
  }, [errorLogin, t]);

  const loginWithGoogle = useCallback(async () => {
    try {
      await signInWithOauth("google");

      gaEvent({ type: "login", payload: { method: "google" } });

      toast.success(t("login:success.toast.title"));
    } catch (error) {
      const code = (error as FirebaseError)?.code as ErrorCodes;

      if (
        code === AuthErrorCodes.POPUP_CLOSED_BY_USER ||
        code === AuthErrorCodes.EXPIRED_POPUP_REQUEST
      ) {
        return;
      }

      logError(error);
      setErrorLogin(error as Error);
    }
  }, [signInWithOauth, gaEvent, t]);

  return (
    <div className="h-screen w-screen bg-primary-50 p-4 overflow-hidden">
      <div className="text-2xl/4 text-foreground font-normal absolute top-2 left-2">
        <ReedyLogo />
      </div>

      <div className="flex items-center justify-center h-full font-sans flex-col">
        <Card className="w-full max-w-md">
          <CardHeader className="text-left">
            <CardTitle className="pb-2">{t("login:title")}</CardTitle>
            <CardDescription className="text-md/6 font-light text-secondary-600">
              {t("login:subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <Button
              onClick={loginWithGoogle}
              variant="tertiary"
              className="w-full justify-center hover:bg-secondary-50"
            >
              <GoogleLogo width={20} height={20} />
              {t("login:buttons.google")}
            </Button>

            <div className="relative flex items-center justify-center">
              <hr className="absolute left-0 right-0 top-1/2 border-secondary-200" />
              <span className="bg-white relative px-2">
                <Text as="span" variant="muted">
                  {t("login:or")}
                </Text>
              </span>
            </div>

            <LoginForm />
          </CardContent>
        </Card>
        <div className="mt-4">
          <Link
            to="/contact-us"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-normal text-secondary-400 flex items-center hover:text-secondary-600"
          >
            <CircleHelp className="inline-block mr-2 size-4" />
            {t("login:support")}
          </Link>
        </div>
      </div>
    </div>
  );
};
